// GraphComponents/StockDonutChart.tsx
import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import api from "../../../../../../utils/api";

ChartJS.register(ArcElement, Tooltip, Legend);

const fetchAllStockPages = async (page = 1, limit = 50) => {
  const resp = await api.get("/api/stock", { params: { page, limit } });
  return resp.data || resp;
};

const StockDonutChart: React.FC = () => {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const first = await fetchAllStockPages(1, 50);
        const total = first.total ?? 0;
        const limit = first.limit ?? 50;
        let allItems = Array.isArray(first.data) ? first.data : [];

        const totalPages = Math.ceil((total || allItems.length) / limit) || 1;
        if (totalPages > 1) {
          const promises = [];
          for (let p = 2; p <= totalPages; p++) promises.push(fetchAllStockPages(p, limit));
          const results = await Promise.all(promises);
          results.forEach(r => { if (Array.isArray(r.data)) allItems = allItems.concat(r.data); });
        }

        if (!cancelled) setCount(allItems.length);

      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to fetch stock data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-center p-4">Loading chart data...</div>;
  if (error) return <div className="text-center p-4 text-red-500">Error: {error}</div>;
  if (count === 0) return <div className="text-center p-4 text-gray-500">No stock data</div>;

  const data = {
    labels: ["Stock Quantity"],
    datasets: [
      {
        data: [count],
        backgroundColor: ["#4CAF50"],
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.label}: ${ctx.raw}`,
        },
      },
    },
    cutout: "70%",
  };

  return (
    <div className="w-full">
      <h3 className="text-start font-semibold mb-3">Total Stock Quantity</h3>
      <div style={{ width: 300, height: 300, margin: "0 auto" }}>
        <Doughnut data={data} options={options} />
      </div>
    </div>
  );
};

export default StockDonutChart;
